import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Spin } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useUser } from "../../contexts/UserContext/UserContext";
import { useToast } from "../../contexts/ToastContext";
import apiClient from "../../helpers/apiClient";
import {
    createCustomerForInsurance,
    getInsuranceCustomerUserId,
    updateCustomerForInsurance,
} from "../insurance/lib/insuranceSaleApi";
import {
    PAYMENT_METHOD_VALUES,
    PaymentMethodSelector,
    buildAdminPaymentOptions,
    fetchReservationById,
    getDefaultPaymentMethod,
} from "../reservations/lib/reservationPayment";
import { completeReservationCheckIn } from "./api/checkInApi";
import CheckInAgreementModal from "./components/CheckInAgreementModal";
import useSolaCardPayment from "../../components/payment/useSolaCardPayment";
import CheckInCaptureSection from "./components/CheckInCaptureSection";
import CheckInRenterProfileSection from "./components/CheckInRenterProfileSection";
import CheckInSummaryCard from "./components/CheckInSummaryCard";
import useCheckInSubmit from "./hooks/useCheckInSubmit";
import {
    buildCheckInSuccessPath,
    startCheckInPaymentFlow,
} from "./payment/startCheckInPaymentFlow";
import { resolveStartReservationGate } from "./utils/startReservationGate";
import {
    applyEarlyCheckInToReservation,
    buildEarlyCheckInInvoice,
    shouldRecalculateEarlyCheckIn,
} from "./utils/earlyCheckIn";
import {
    INSURANCE_PROVIDERS,
    startInsuranceForCheckIn,
} from "./utils/insuranceHelpers";
import { formatBookedDate } from "./utils/reservationHelpers";

const RENTER_PROFILE_BASE_FIELDS = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "dateOfBirth",
    "licenseNumber",
    "licenseState",
    "licenseExpiration",
];

const RENTER_PROFILE_BONZAH_FIELDS = ["addressLine1", "zipCode"];

const getRenterProfileFieldNames = (provider) => {
    const isBonzah = String(provider).toLowerCase() === "bonzah";
    return isBonzah ? [...RENTER_PROFILE_BASE_FIELDS, ...RENTER_PROFILE_BONZAH_FIELDS] : RENTER_PROFILE_BASE_FIELDS;
};

const CheckInContent = ({ reservationId }) => {
    const { t } = useTranslation(["checkIn", "reservationPayment", "reservations"]);
    const tRef = useRef(t);
    tRef.current = t;
    const history = useHistory();
    const location = useLocation();
    const toast = useToast();
    const { myCompanyData: companyData } = useUser();
    const { submitCheckIn } = useCheckInSubmit();
    const { requestSolaCardPayment, solaCardModal } = useSolaCardPayment();

    const defaultPaymentGateway = companyData?.defaultPaymentGateway || "stripe";
    const showCashPayment = companyData?.preferences?.showCashPayment === true;
    const isEarlyCheckInRequest = new URLSearchParams(location.search).get("earlyCheckIn") === "true";

    const checkInCaptureRef = useRef(null);
    const [renterForm] = Form.useForm();
    const [reservation, setReservation] = useState(null);
    const [renterGate, setRenterGate] = useState(null);
    const [renterGateLoading, setRenterGateLoading] = useState(false);
    const [earlyCheckInPayload, setEarlyCheckInPayload] = useState(null);
    const [earlyCheckInError, setEarlyCheckInError] = useState(null);
    const [recalculatingInvoice, setRecalculatingInvoice] = useState(false);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSavingRenterProfile, setIsSavingRenterProfile] = useState(false);
    const [isAgreementModalOpen, setIsAgreementModalOpen] = useState(false);
    const [paymentSystem, setPaymentSystem] = useState(null);

    const paymentOptions = useMemo(
        () =>
            buildAdminPaymentOptions({
                defaultPaymentGateway,
                showCashPayment,
                t,
            }),
        [defaultPaymentGateway, showCashPayment, t],
    );

    const displayReservation = useMemo(() => {
        if (!reservation) return null;
        if (!earlyCheckInPayload) return reservation;
        return applyEarlyCheckInToReservation(reservation, earlyCheckInPayload);
    }, [reservation, earlyCheckInPayload]);

    const dueAmount = displayReservation?.invoice?.due || 0;
    const showPaymentSection = dueAmount > 0;

    useEffect(() => {
        setPaymentSystem(null);
    }, [reservationId]);

    useEffect(() => {
        if (!showPaymentSection) {
            setPaymentSystem(null);
            return;
        }
        if (!paymentSystem && paymentOptions.length > 0) {
            setPaymentSystem(getDefaultPaymentMethod(paymentOptions));
        }
    }, [paymentOptions, paymentSystem, showPaymentSection]);

    useEffect(() => {
        let cancelled = false;

        const loadReservation = async () => {
            try {
                setLoading(true);
                const next = await fetchReservationById(reservationId);
                if (cancelled) return;

                if (!next) {
                    setNotFound(true);
                    setReservation(null);
                    setLoading(false);
                    return;
                }

                setReservation({ ...next, id: next.id || reservationId });
                setNotFound(false);
            } catch {
                if (!cancelled) {
                    toast.error({ message: tRef.current("loadError") });
                    setNotFound(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadReservation();
        return () => {
            cancelled = true;
        };
    }, [toast, reservationId]);

    useEffect(() => {
        if (!reservation || !isEarlyCheckInRequest || !shouldRecalculateEarlyCheckIn(reservation, true)) {
            return undefined;
        }

        if (earlyCheckInPayload) return undefined;

        let cancelled = false;

        const recalculateEarlyCheckInInvoice = async () => {
            setRecalculatingInvoice(true);
            setEarlyCheckInError(null);

            try {
                const payload = await buildEarlyCheckInInvoice(reservation);
                if (cancelled) return;
                setEarlyCheckInPayload(payload);
            } catch (error) {
                if (cancelled) return;
                setEarlyCheckInError(
                    error?.response?.data?.message || error?.message || tRef.current("earlyCheckInInvoiceError"),
                );
            } finally {
                if (!cancelled) setRecalculatingInvoice(false);
            }
        };

        recalculateEarlyCheckInInvoice();
        return () => {
            cancelled = true;
        };
    }, [earlyCheckInPayload, isEarlyCheckInRequest, reservation]);

    const showRenterProfileSection = Boolean(renterGate && renterGate.canProceed === false);

    const resolveRenterGateState = useCallback(async () => {
        if (!displayReservation) {
            return null;
        }
        return resolveStartReservationGate(displayReservation, companyData);
    }, [companyData, displayReservation]);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!displayReservation) {
                if (!cancelled) setRenterGate(null);
                return;
            }

            setRenterGateLoading(true);
            try {
                const gate = await resolveRenterGateState();
                if (!cancelled) setRenterGate(gate);
            } finally {
                if (!cancelled) setRenterGateLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [displayReservation, resolveRenterGateState]);

    const saveRenterProfileIfRequired = async () => {
        if (!showRenterProfileSection || !renterGate) {
            return true;
        }

        const { provider } = renterGate;
        const fieldNames = getRenterProfileFieldNames(provider);

        try {
            const values = await renterForm.validateFields(fieldNames);
            const userId = getInsuranceCustomerUserId(renterGate.customer);

            if (userId) {
                await updateCustomerForInsurance(userId, values);
            } else {
                await createCustomerForInsurance(values);
            }

            const nextGate = await resolveRenterGateState();
            if (nextGate?.missingDetails?.length > 0) {
                setRenterGate(nextGate);
                toast.error({
                    message: t("checkInRenterProfileStillIncomplete"),
                    description: nextGate.missingDetails.join(", "),
                });
                return false;
            }

            setRenterGate(nextGate ? { ...nextGate, canProceed: true } : { canProceed: true });
            return true;
        } catch (error) {
            if (error?.errorFields) {
                return false;
            }

            toast.error({
                message: t("loadError"),
                description: error?.response?.data?.message || error?.message,
            });
            return false;
        }
    };

    const canConfirmCheckIn =
        !isSubmitting &&
        !isSavingRenterProfile &&
        !renterGateLoading &&
        (!showPaymentSection || Boolean(paymentSystem));

    const isConfirmCheckInBusy = isSubmitting || isSavingRenterProfile;

    const getStoredAgreementUrl = (reservationRecord) =>
        reservationRecord?.agreementDocUrl || reservationRecord?.agreementUrl || "";

    const completeCheckIn = async (agreementDocUrl) => {
        if (!displayReservation || !agreementDocUrl) return;

        try {
            setIsSubmitting(true);

            const currentDueAmount = displayReservation.invoice?.due || 0;
            const selectedPaymentSystem = paymentSystem || defaultPaymentGateway || "stripe";
            const preserveOngoing = currentDueAmount > 0 && selectedPaymentSystem !== PAYMENT_METHOD_VALUES.CASH;
            const checkInData = checkInCaptureRef.current?.getData();

            if (!checkInData) {
                toast.error({ message: t("loadError") });
                return;
            }

            const { insuranceType } = displayReservation;
            if (currentDueAmount <= 0 && INSURANCE_PROVIDERS.includes(insuranceType)) {
                await startInsuranceForCheckIn(displayReservation.id, insuranceType, toast);
            }

            await submitCheckIn(
                displayReservation,
                {
                    ...checkInData,
                    preserveOngoing,
                    ...(earlyCheckInPayload
                        ? {
                              earlyPickupDateTime: earlyCheckInPayload.pickupDateTime,
                              earlyCheckInInvoice: earlyCheckInPayload.invoice,
                          }
                        : {}),
                },
                toast,
            );

            const { data: updatedRes } = await apiClient.get(`/api/reservation/${displayReservation.id}`);
            const updatedReservation = updatedRes?.data;
            if (!updatedReservation) {
                throw new Error("Failed to refresh reservation after check-in");
            }

            const nextDueAmount = updatedReservation.invoice?.due || 0;

            if (nextDueAmount > 0) {
                if (selectedPaymentSystem === PAYMENT_METHOD_VALUES.CASH) {
                    if (INSURANCE_PROVIDERS.includes(insuranceType)) {
                        await startInsuranceForCheckIn(displayReservation.id, insuranceType, toast);
                    }
                    await startCheckInPaymentFlow({
                        reservation: updatedReservation,
                        dueAmount: nextDueAmount,
                        paymentSystem: PAYMENT_METHOD_VALUES.CASH,
                        agreementDocUrl,
                        companyData,
                        history,
                        toast,
                        t,
                        requestSolaCardPayment,
                    });
                    setIsAgreementModalOpen(false);
                    return;
                }

                await startCheckInPaymentFlow({
                    reservation: updatedReservation,
                    dueAmount: nextDueAmount,
                    paymentSystem: selectedPaymentSystem,
                    agreementDocUrl,
                    companyData,
                    history,
                    toast,
                    t,
                    requestSolaCardPayment,
                });
                return;
            }

            await completeReservationCheckIn(displayReservation.id, { agreementDocUrl });
            setIsAgreementModalOpen(false);
            history.push(buildCheckInSuccessPath(displayReservation.id));
        } catch (error) {
            toast.error({
                message: t("checkInFailed"),
                description: error?.response?.data?.message || error?.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmCheckIn = async () => {
        if (!displayReservation || isConfirmCheckInBusy) return;

        if (showPaymentSection && !paymentSystem) {
            toast.error({ message: t("selectPaymentRequired") });
            return;
        }

        const checkInData = checkInCaptureRef.current?.getData();
        if (!checkInData) {
            toast.error({ message: t("loadError") });
            return;
        }

        try {
            if (showRenterProfileSection) {
                setIsSavingRenterProfile(true);
                const profileReady = await saveRenterProfileIfRequired();
                if (!profileReady) {
                    return;
                }
            }

            const existingAgreementUrl = getStoredAgreementUrl(displayReservation);
            if (existingAgreementUrl) {
                await completeCheckIn(existingAgreementUrl);
                return;
            }

            setIsAgreementModalOpen(true);
        } finally {
            setIsSavingRenterProfile(false);
        }
    };

    if (loading || recalculatingInvoice) {
        return (
            <div className="flex min-h-[320px] items-center justify-center py-12">
                <Spin size="large" tip={t("loadingReservation")} />
            </div>
        );
    }

    if (earlyCheckInError) {
        return (
            <div className="rounded-2xl border border-[#E6EAF2] bg-white p-8 text-center">
                <p className="fontSemi text-lg text-[#101828]">{t("earlyCheckInInvoiceErrorTitle")}</p>
                <p className="fontRegular mt-2 text-sm text-[#667085]">{earlyCheckInError}</p>
                <Button type="primary" className="!mt-5 !rounded-xl" onClick={() => history.push(`/reservations/${reservationId}`)}>
                    {t("backToReservation")}
                </Button>
            </div>
        );
    }

    if (notFound || !displayReservation) {
        return (
            <div className="rounded-2xl border border-[#E6EAF2] bg-white p-8 text-center">
                <p className="fontSemi text-lg text-[#101828]">{t("notFoundTitle")}</p>
                <p className="fontRegular mt-2 text-sm text-[#667085]">{t("notFoundBody")}</p>
                <Button type="primary" className="!mt-5 !rounded-xl" onClick={() => history.push("/reservations")}>
                    {t("backToReservations")}
                </Button>
            </div>
        );
    }

    const bookedOn = formatBookedDate(displayReservation?.createdAt);

    return (
        <div className="flex min-w-0 flex-col gap-3">
            {solaCardModal}
            <div className="mb-2 flex min-w-0 items-start gap-3 sm:items-center sm:gap-4">
                <Button
                    type="text"
                    size="large"
                    icon={<ArrowLeftOutlined className="text-xl sm:text-2xl" />}
                    onClick={() => history.push(`/reservations/${reservationId}`)}
                    className="!h-auto shrink-0 !border-0 !bg-transparent !p-0 !text-[#667085] hover:!text-[#5D5DF6]"
                />
                <div className="min-w-0">
                    <h1 className="fontSemi text-2xl text-[#101828] sm:text-3xl md:text-4xl">{t("pageTitle")}</h1>
                    <p className="fontRegular text-sm text-[#667085]">
                        #{reservationId} · {t("bookedOn")} {bookedOn}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px] xl:gap-8">
                <div className="order-2 min-w-0 space-y-6 sm:space-y-8 lg:order-1">
                    <div className="rounded-2xl border border-[#EEF0F6] bg-white p-4 shadow-[0_8px_30px_rgba(17,24,39,0.06)] sm:p-5 md:p-8">
                        <CheckInCaptureSection ref={checkInCaptureRef} resetKey={reservationId} t={t} />

                        {showPaymentSection ? (
                            <PaymentMethodSelector
                                title={t("paymentTitle")}
                                subtitle={t("paymentSubtitle")}
                                options={paymentOptions}
                                value={paymentSystem}
                                onChange={setPaymentSystem}
                            />
                        ) : null}

                        {showRenterProfileSection ? (
                            <CheckInRenterProfileSection
                                form={renterForm}
                                provider={renterGate.provider}
                                customer={renterGate.customer}
                                missingDetails={renterGate.missingDetails}
                                verificationStatus={renterGate.verificationStatus}
                            />
                        ) : null}
                    </div>

                    <Button
                        type="primary"
                        size="large"
                        block
                        loading={isConfirmCheckInBusy}
                        disabled={!canConfirmCheckIn}
                        onClick={handleConfirmCheckIn}
                        className="!h-12 !rounded-xl !px-8 !font-semibold sm:!w-auto sm:!inline-flex">
                        {t("confirmCheckIn")}
                    </Button>
                </div>

                <div className="order-1 lg:order-2">
                    <CheckInSummaryCard reservation={displayReservation} t={t} />
                </div>
            </div>

            <CheckInAgreementModal
                open={isAgreementModalOpen}
                confirming={isSubmitting}
                reservation={displayReservation}
                onCancel={() => {
                    if (!isSubmitting) setIsAgreementModalOpen(false);
                }}
                onAgreeAndContinue={completeCheckIn}
            />
        </div>
    );
};

CheckInContent.propTypes = {
    reservationId: PropTypes.string.isRequired,
};

export default CheckInContent;
